import { useState } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Preview } from "components/PreviewModal";
import { MenuDotsVertical } from "components/icons/MenuDotsVertical";
import {
  BenefitCategory,
  TaxBenefitBreakdownEntry,
} from "types/Models/estimatedTax";
import { currency } from "utils/Currency";
import {
  BENEFIT_BREAKDOWN_AMOUNT_COLUMN_LABEL,
  BENEFIT_BREAKDOWN_SOURCE_SETTINGS,
  BENEFIT_BREAKDOWN_SOURCE_MANUAL,
  BENEFIT_BREAKDOWN_SOURCE_RETURN_EXTRACTION,
  BENEFIT_BREAKDOWN_SOURCE_TAX_BENEFITS,
  DELETE_VARIANT_BENEFIT_ENTRY,
  DELETE_VARIANT_S174_BENEFIT_ENTRY,
  DELETE_VARIANT_S174_ENTRY,
} from "./constants";
import {
  DeleteBenefitEntryModal,
  DeleteBenefitEntryVariant,
} from "./DeleteBenefitEntryModal";

type Props = {
  entries: TaxBenefitBreakdownEntry[];
  entityId: string;
  category: BenefitCategory;
};

const getYearLabel = (entry: TaxBenefitBreakdownEntry): string => {
  const year = entry.tax_year ?? entry.tax_year_started;
  if (year == null) return "—";
  if (entry.source === BENEFIT_BREAKDOWN_SOURCE_SETTINGS)
    return `Before ${year}`;
  return String(year);
};

const getSourceLabel = (entry: TaxBenefitBreakdownEntry): string => {
  if (entry.source === BENEFIT_BREAKDOWN_SOURCE_SETTINGS)
    return "Entered when you were onboarded to Estimated Tax";
  if (entry.source === BENEFIT_BREAKDOWN_SOURCE_RETURN_EXTRACTION)
    return `Fetched from your ${entry.tax_year} return`;
  if (
    entry.source === BENEFIT_BREAKDOWN_SOURCE_MANUAL ||
    entry.source === BENEFIT_BREAKDOWN_SOURCE_TAX_BENEFITS
  )
    return "Entered manually";
  return "Fetched from your books";
};

const getReturnDocumentId = (entry: TaxBenefitBreakdownEntry): string | null =>
  entry.source === BENEFIT_BREAKDOWN_SOURCE_RETURN_EXTRACTION
    ? entry.source_document_id ?? null
    : null;

const BreakdownSource = ({ entry }: { entry: TaxBenefitBreakdownEntry }) => {
  const isFromReturn =
    entry.source === BENEFIT_BREAKDOWN_SOURCE_RETURN_EXTRACTION;

  if (isFromReturn) {
    return (
      <span className="t-w-fit">
        <Tag tagType="purple" icon={false} rounded size="small">
          From filed return
        </Tag>
      </span>
    );
  }

  return (
    <span className="t-text-body-sm-italic t-italic t-text-neutral-40">
      {getSourceLabel(entry)}
    </span>
  );
};

const getAmount = (entry: TaxBenefitBreakdownEntry): number =>
  entry.original_amount ?? entry.amount ?? 0;

// SETTINGS-sourced entries (onboarding data) are only managed from the
// Settings screen, not deletable here from Tax Benefits.
const DELETABLE_SOURCES = new Set<TaxBenefitBreakdownEntry["source"]>([
  BENEFIT_BREAKDOWN_SOURCE_MANUAL,
  BENEFIT_BREAKDOWN_SOURCE_TAX_BENEFITS,
  BENEFIT_BREAKDOWN_SOURCE_RETURN_EXTRACTION,
]);

const isDeletable = (entry: TaxBenefitBreakdownEntry): boolean =>
  entry.entry_id != null && DELETABLE_SOURCES.has(entry.source);

const getDeleteVariant = (
  entry: TaxBenefitBreakdownEntry
): DeleteBenefitEntryVariant => {
  if (
    entry.source === BENEFIT_BREAKDOWN_SOURCE_TAX_BENEFITS ||
    entry.source === BENEFIT_BREAKDOWN_SOURCE_RETURN_EXTRACTION
  )
    return DELETE_VARIANT_S174_BENEFIT_ENTRY;
  if (entry.source === BENEFIT_BREAKDOWN_SOURCE_SETTINGS)
    return DELETE_VARIANT_S174_ENTRY;
  return DELETE_VARIANT_BENEFIT_ENTRY;
};

export const BenefitBreakdownTable = ({
  entries,
  entityId,
  category,
}: Props) => {
  const [deletingEntry, setDeletingEntry] = useState<{
    entryId: string;
    variant: DeleteBenefitEntryVariant;
  } | null>(null);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(
    null
  );

  const closePreview = () => setPreviewDocumentId(null);

  const handleViewReturn = (entry: TaxBenefitBreakdownEntry) =>
    setPreviewDocumentId(getReturnDocumentId(entry));

  const handleDelete = (entry: TaxBenefitBreakdownEntry) => {
    if (entry.entry_id == null) return;
    setDeletingEntry({
      entryId: entry.entry_id,
      variant: getDeleteVariant(entry),
    });
  };

  return (
    <>
      <Table.Container sticky={false}>
        <Table.Content className="t-text-body-sm">
          <Table.Head>
            <Table.HeadRow>
              <Table.HeadCell isUpperCase={false}>Year & Source</Table.HeadCell>
              <Table.HeadCell
                isUpperCase={false}
                className="t-text-right t-pr-10"
              >
                {BENEFIT_BREAKDOWN_AMOUNT_COLUMN_LABEL[category]}
              </Table.HeadCell>
            </Table.HeadRow>
          </Table.Head>
          <Table.Body>
            {entries.map((entry, index) => (
              <Table.Row
                key={
                  entry.entry_id ??
                  `${entry.source}-${
                    entry.tax_year ?? entry.tax_year_started ?? "none"
                  }-${getAmount(entry)}-${index}`
                }
              >
                <Table.Cell>
                  <div className="t-flex t-flex-col">
                    <span className="t-text-body t-text-text-60">
                      {getYearLabel(entry)}
                    </span>
                    <BreakdownSource entry={entry} />
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="t-flex t-items-center t-justify-end t-gap-2">
                    <span className="t-text-body t-text-text-60">
                      {currency({
                        amount: getAmount(entry),
                        maximumFractionDigits: 0,
                      })}
                    </span>
                    <Dropdown.Root>
                      <Dropdown.Trigger asChild>
                        <div>
                          <Button customType="ghost_icon" size="small">
                            <MenuDotsVertical />
                          </Button>
                        </div>
                      </Dropdown.Trigger>
                      <Dropdown.Portal>
                        <Dropdown.Content side="bottom" align="end">
                          {getReturnDocumentId(entry) && (
                            <Dropdown.Item
                              onSelect={() => handleViewReturn(entry)}
                            >
                              View return
                            </Dropdown.Item>
                          )}
                          <Dropdown.Item
                            type="danger"
                            disabled={!isDeletable(entry)}
                            onSelect={() => handleDelete(entry)}
                          >
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Content>
                      </Dropdown.Portal>
                    </Dropdown.Root>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.Container>

      {deletingEntry && (
        <DeleteBenefitEntryModal
          open
          onClose={() => setDeletingEntry(null)}
          entityId={entityId}
          entryId={deletingEntry.entryId}
          variant={deletingEntry.variant}
        />
      )}

      <Preview
        showModal={previewDocumentId !== null}
        closeModal={closePreview}
        previewId={previewDocumentId}
      />
    </>
  );
};
